/**
 * UsersView
 *
 * Top users table showing aggregated metrics per user.
 * Click on a user row to drill down (sets userFilter).
 */

import { useTranslation } from '@/hooks/useTranslation';
import type { UserBreakdown } from '@/services/metricsAPI';

interface UsersViewProps {
  users: UserBreakdown[];
  loading: boolean;
  onFilterByUser: (userId: string) => void;
}

const formatSeconds = (s: number): string => {
  if (s < 60) return `${s.toFixed(1)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${(s % 60).toFixed(0)}s`;
};

const formatDate = (d: string | null): string => (d ? new Date(d).toLocaleString() : '-');

export function UsersView({ users, loading, onFilterByUser }: UsersViewProps): JSX.Element {
  const { t } = useTranslation();

  return (
    <div className="panel-glass-card-lg">
      <h2 className="section-heading-lg">{t('admin.metrics.usersView')}</h2>

      {loading ? (
        <div className="text-white/50 text-center py-8">{t('common.loading')}</div>
      ) : users.length === 0 ? (
        <div className="text-white/50 text-center py-8">{t('admin.metrics.noData')}</div>
      ) : (
        <div className="overflow-x-auto -mx-4 sm:mx-0">
          <table className="w-full text-sm text-white/80">
            <thead className="text-white/50 border-b border-white/10">
              <tr>
                <th className="text-left py-3 px-3">{t('admin.metrics.rank')}</th>
                <th className="text-left py-3 px-3">{t('admin.metrics.userName')}</th>
                <th className="text-left py-3 px-3">{t('admin.metrics.userEmail')}</th>
                <th className="text-right py-3 px-3">{t('admin.metrics.operationCount')}</th>
                <th className="text-right py-3 px-3">{t('admin.metrics.totalSpent')}</th>
                <th className="text-right py-3 px-3">{t('admin.metrics.avgTokens')}</th>
                <th className="text-right py-3 px-3">{t('admin.metrics.duration')}</th>
                <th className="text-center py-3 px-3">{t('admin.metrics.lastActive')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {users.map((user, idx) => (
                <tr
                  key={user.user_id}
                  className="hover:bg-white/5 cursor-pointer"
                  onClick={() => onFilterByUser(user.user_id)}
                >
                  <td className="py-3 px-3 text-white/50">#{idx + 1}</td>
                  <td className="py-3 px-3 font-medium">{user.user_display_name ?? '-'}</td>
                  <td className="py-3 px-3">{user.user_email ?? '-'}</td>
                  <td className="py-3 px-3 text-right">{user.total_operations}</td>
                  <td className="py-3 px-3 text-right">{user.total_tokens.toLocaleString()}</td>
                  <td className="py-3 px-3 text-right">
                    {user.total_operations > 0
                      ? Math.round(user.total_tokens / user.total_operations).toLocaleString()
                      : '-'}
                  </td>
                  <td className="py-3 px-3 text-right">{formatSeconds(user.total_seconds)}</td>
                  <td className="py-3 px-3 text-center text-xs">{formatDate(user.last_active)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
